import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import debounce from 'lodash.debounce'
import PersonRemove from '@mui/icons-material/PersonRemove'
import { Stack, Button, Tooltip } from '@mui/material'
import { useStytchIsAuthorized, useStytchMember } from '@stytch/react/b2b'
import { Member } from '@stytch/vanilla-js'
import { QueryKey, useQueryClient } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import {
  useGetAuthRoles,
  useUpdateAuthUser,
} from '@/service-library/hooks/auth-user'
import { useInviteMember } from '@/services/hooks/stytch'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { AuthUser } from '@/types/users'
import {
  getHighestRole,
  DEFAULT_USER_ROLE,
  getRemoveUserPermission,
} from '@/utils/auth-user'
import { showErrorSnackbar, showInfoSnackbar } from '@/utils/snackbars'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import { FormTextField, PixyDocsForm } from '@/components/forms'
import LabeledData from '@/components/labeled-data/LabeledData'
import LargeHeading from '@/components/large-heading/LargeHeading'
import RemoveUserDialog from './RemoveUserDialog'
import UserRolesPicker from './UserRolesPicker'

type FormValues = {
  employee_ext_id: string
  role_id: string
}

type UserDetailsProps = {
  user: AuthUser
  queryKey: QueryKey
}

const primeAdminsMap = {
  'tyson@pixydocs.com': 'Lord Emperor of the Eternal Mandate',
  'carl@pixydocs.com': 'Sovereign Printus Maximus',
  'toby@pixydocs.com': 'Highprince of Laterculus Modularis Lēgōnis',
  'zach@pixydocs.com': 'Supreme Strategos of the Pixy Imperium',
  'joyce@pixydocs.com': 'Sentinel Grand Vizier',
  'chad@pixydocs.com': 'Praetor Chaddus Radicus, First of His Name',
  'josh@pixydocs.com': 'Supreme Artificer',
  'artem@pixydocs.com': 'Duke of Quiet Aptitude',
  'sam@pixydocs.com': 'da guy who does da sales',
}

export default function UserDetails({ user, queryKey }: UserDetailsProps) {
  const navigate = useNavigate()
  const { member: loggedInMember } = useStytchMember()
  const isSuperUser = useIsSuperUser()
  const queryClient = useQueryClient()
  const removeOverlay = useOverlay()

  const { inviteMember, isPending: isInvitePending } = useInviteMember({
    onSuccess: () => {
      showInfoSnackbar('Invitation email has been resent.')
    },
    onError: () => {
      showErrorSnackbar(
        'Unable to resend invitation email. Please try again later.',
      )
    },
  })

  const { isAuthorized: canEditRoles } = useStytchIsAuthorized(
    'pd.user',
    'edit.roles',
  )

  const { roles } = useGetAuthRoles()
  const rolesMap = useMemo(
    () => new Map(roles.map((role) => [role.id, role])),
    [roles],
  )

  const userKey = user.id ?? user.stytch_member?.member_id

  const userRole = useMemo(
    () => getHighestRole(user.roles ?? [], rolesMap),
    [user.roles, rolesMap],
  )
  const loggedInMemberRole = useMemo(
    () => getHighestRole(loggedInMember?.roles || [], rolesMap),
    [loggedInMember?.roles, rolesMap],
  )

  const canUpdateRole = useMemo(() => {
    if (!user.roles || !canEditRoles || userRole.id === 'Prime Admin')
      return false
    return loggedInMemberRole.order >= userRole.order
  }, [
    user.roles,
    canEditRoles,
    loggedInMemberRole.order,
    userRole.id,
    userRole.order,
  ])

  const defaultValues = useMemo(
    () => ({
      employee_ext_id: user.employee_ext_id ?? '',
      role_id: userRole.id,
    }),
    [user.employee_ext_id, userRole.id],
  )

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
  })

  const { subscribe, reset } = methods

  const { updateAuthUser } = useUpdateAuthUser({
    onError: () => {
      reset()
      showErrorSnackbar('Unable to update user roles. Please try again later.')
    },
    onSettled: () => {
      // Invalidate the users list to get updated data
      if (queryKey) queryClient.invalidateQueries({ queryKey })
    },
  })

  const debouncedUpdateUser = useMemo(
    () => debounce(updateAuthUser, 1000),
    [updateAuthUser],
  )

  const isLoggedInMember = user.id
    ? loggedInMember?.external_id === user.id
    : loggedInMember?.member_id === user.stytch_member?.member_id
  const { canRemoveUser, tooltip: removeTooltip } = user.stytch_member
    ? getRemoveUserPermission({
        loggedInMember: loggedInMember as Member,
        targetMember: user.stytch_member,
        rolesMap,
      })
    : { canRemoveUser: false, tooltip: 'Remove user' }

  useEffect(() => {
    const callback = subscribe({
      formState: {
        values: true,
        isDirty: true,
      },
      callback: ({ values, isDirty, dirtyFields }) => {
        if (!isDirty) {
          debouncedUpdateUser.cancel()
          return
        }

        if (!userKey) return

        const updatePayload: {
          id: string
          roles_ids?: string[]
          employee_ext_id?: string
        } = {
          id: userKey,
        }

        let hasUpdates = false

        if (dirtyFields?.role_id) {
          const newRoleId = values.role_id
          updatePayload.roles_ids =
            newRoleId === DEFAULT_USER_ROLE.id ? [] : [newRoleId]
          hasUpdates = true
        }

        if (dirtyFields?.employee_ext_id) {
          updatePayload.employee_ext_id = values.employee_ext_id
          hasUpdates = true
        }

        if (hasUpdates) {
          debouncedUpdateUser(updatePayload)
        }
      },
    })
    return () => callback()
  }, [debouncedUpdateUser, subscribe, userKey])

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  if (!loggedInMember) return null

  return (
    <Stack spacing={2}>
      <div>
        <LargeHeading
          heading={user.name}
          subHeading={`User${isLoggedInMember ? ' (You)' : ''}`}
          actions={
            <>
              {user.email &&
                ['pending', 'invited'].includes(user.status ?? '') && (
                  <Button
                    disabled={isInvitePending}
                    onClick={() => {
                      inviteMember({ email_address: user.email as string })
                    }}
                    size="small"
                    variant="text"
                  >
                    Resend Email
                  </Button>
                )}
              {userKey && <CopyIDButton stringToCopy={userKey} />}
              <Tooltip enterDelay={500} title={removeTooltip}>
                <div>
                  <Button
                    color="error"
                    disabled={!canRemoveUser}
                    onClick={removeOverlay.open}
                    size="small"
                    startIcon={<PersonRemove />}
                    variant="text"
                  >
                    Remove
                  </Button>
                </div>
              </Tooltip>
            </>
          }
        />
      </div>
      <Stack spacing={3}>
        <LabeledData label="Name" data={user.name} />
        <LabeledData label="Email" data={user.email} />
        {canUpdateRole ? (
          <PixyDocsForm
            methods={methods}
            onSubmit={() => {}}
            style={{ width: 300 }}
          >
            <Stack spacing={3}>
              <FormTextField
                name="employee_ext_id"
                fullWidth
                label="Employee ID"
              />
              <UserRolesPicker />
            </Stack>
          </PixyDocsForm>
        ) : (
          <>
            <LabeledData
              label="Employee ID"
              data={user.employee_ext_id ?? undefined}
            />
            <LabeledData
              label="Role"
              data={
                isSuperUser
                  ? // @ts-expect-error -- TODO: remove primeAdminsMap at some point
                    primeAdminsMap[user.email ?? ''] || userRole.id
                  : userRole.id
              }
            />
          </>
        )}
      </Stack>
      <RemoveUserDialog
        overlay={removeOverlay}
        member={user.stytch_member ?? null}
        onRemoved={() => {
          navigate('/settings/users')
        }}
      />
    </Stack>
  )
}
